import React, { useEffect, useRef, useState } from 'react';
import { hasOwnProperty } from '@go-shared/lib/utils';
import { DefaultedText } from '../DefaultedText';
import BubbleInput, { BubbleInputProps } from './BubbleInput';
import BubbleButton from './BubbleButton';

import './BubbleHintSelect.scss';

interface BubbleHintSelectPropsBase<T> {
  variant?: BubbleInputProps['variant'];
  value?: T;
  options?: T[];
  block?: boolean;
  readOnly?: boolean;
  menuClassName?: string;
  name?: string;
  keyExtractor?: (value: T) => string;
  labelExtractor?: (value: T) => string;
  internalValueExtractor?: (value: T) => string;
  undefinedLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  subtitle?: string;
}

interface BubbleHintSelectPropsWithUndefined<T> {
  allowUndefined: true;
  onValue?: (value?: T) => void;
}

interface BubbleHintSelectPropsNoUndefined<T> {
  allowUndefined: false;
  onValue?: (value: T) => void;
}

const __UNDEFINED__ = '__UNDEFINED__';

export type BubbleHintSelectProps<T> = React.HTMLAttributes<HTMLDivElement> &
  BubbleHintSelectPropsBase<T> &
  (BubbleHintSelectPropsWithUndefined<T> | BubbleHintSelectPropsNoUndefined<T>);

export default function BubbleHintSelect<T extends string | number | object>(
  props: BubbleHintSelectProps<T>
) {
  const {
    // from component
    value,
    options: propOptions,
    block,
    keyExtractor,
    labelExtractor,
    internalValueExtractor,
    readOnly,
    onValue,
    name,
    menuClassName,
    allowUndefined,
    undefinedLabel,
    variant,
    placeholder,
    disabled,
    subtitle,
    // from element
    className: propClassName,
    style: propStyle,
    ...rest
  } = props;

  const options = propOptions || [];

  const classes = ['bubble-hintselect-container'];
  if (block) classes.push('full');
  if (propClassName) classes.push(propClassName);

  let style: React.CSSProperties = {};
  if (propStyle) style = { ...style, ...propStyle };

  const menuClasses = ['bubble-hintselect-menu bg-white py-1 shadow rounded-br rounded-bl'];
  if (menuClassName) menuClasses.push(menuClassName);

  const internalValue = value === undefined ? __UNDEFINED__ : resolveInternalValue(value);
  const currentOption = options.find(o => resolveInternalValue(o) === internalValue);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const [search, setSearch] = useState('');
  const [inputDirty, setInputDirty] = useState(false);
  const [inputHasFocus, setInputHasFocus] = useState(false);

  useEffect(() => {
    calculateFocusStates();
  }, [value]);

  function resolveInternalValue(val: T) {
    if (internalValueExtractor) return internalValueExtractor(val);
    else if (typeof val === 'object') return JSON.stringify(val);
    else return `${val}`;
  }

  function resolveLabel(val: T) {
    if (labelExtractor) return labelExtractor(val);
    else return resolveInternalValue(val);
  }

  function resolveKey(val: T) {
    if (keyExtractor) {
      return keyExtractor(val);
    } else if (hasOwnProperty(val, 'key') && ['string', 'number'].includes(typeof val.key)) {
      return `${val.key}`;
    } else {
      return resolveInternalValue(val);
    }
  }

  function onSelectItem(e: React.MouseEvent<HTMLButtonElement>, val: T | undefined) {
    e.preventDefault();
    e.stopPropagation();

    if (allowUndefined && onValue) {
      onValue(val === __UNDEFINED__ ? undefined : val);
    } else if (!allowUndefined && onValue) {
      onValue(val || options[0]);
    }

    inputRef.current?.blur();
  }

  function calculateFocusStates() {
    setSearch(value ? resolveLabel(value) : '');
    setInputDirty(false);
    setInputHasFocus(inputRef.current ? document.activeElement === inputRef.current : false);
  }

  function onInputHandler(text: string) {
    setSearch(text);
    setInputDirty(true);
  }

  function onChangeHandler(e: React.ChangeEvent<HTMLInputElement>) {
    // Auto-select if the value exactly matches one option (e.g. browser autofill).
    // Only trigger when the user wasn't already typing (inputDirty is false).
    if (inputDirty) return;
    const text = e.target.value;
    const exactMatch = options.find(o => resolveLabel(o).toLowerCase() === text.toLowerCase());
    if (exactMatch && onValue) {
      if (allowUndefined) {
        onValue(exactMatch);
      } else {
        (onValue as (value: T) => void)(exactMatch);
      }
      inputRef.current?.blur();
    }
  }

  const filteredOptions = !inputDirty
    ? options
    : options.filter(o => resolveLabel(o).toLowerCase().includes(search.toLowerCase()));

  return (
    <div className={classes.join(' ')} style={style}>
      {readOnly ? (
        <DefaultedText
          val={currentOption ? `${resolveLabel(currentOption)}` : undefined}
          def="None"
        />
      ) : (
        <div {...rest}>
          <BubbleInput
            variant={variant}
            inputRef={inputRef}
            type="text"
            block
            name={name}
            placeholder={placeholder}
            inputClassName={inputDirty ? 'text-crimson' : ''}
            value={search}
            onValue={onInputHandler}
            onChange={onChangeHandler}
            onFocus={calculateFocusStates}
            onBlur={calculateFocusStates}
            disabled={disabled}
            subtitle={subtitle}
          />
          {inputHasFocus && (
            <div className={menuClasses.join(' ')}>
              {allowUndefined && (
                <BubbleButton
                  block
                  variant="crimson"
                  inverse
                  className="menu-button text-left font-normal-i"
                  allowWrap
                  onMouseDown={e => onSelectItem(e, undefined)}
                >
                  {undefinedLabel || 'None'}
                </BubbleButton>
              )}
              {filteredOptions.map(o => (
                <BubbleButton
                  className="menu-button text-left font-normal-i"
                  block
                  variant="black"
                  key={resolveKey(o)}
                  inverse
                  allowWrap
                  onMouseDown={e => onSelectItem(e, o)}
                >
                  {resolveLabel(o)}
                </BubbleButton>
              ))}
              {filteredOptions.length < 1 && (
                <div className="px-4 py-2 italic text-smoke">No results found</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
